import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// Initialize SweetAlert2 with React content support
const ReactSwal = withReactContent(Swal);

/**
 * Centralized Alert Service for the entire application
 * Replaces all console alert/confirm messages with SweetAlert2
 */
export class AlertService {
  
  /**
   * Show a success message
   * @param {string} title - Main title text
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   */
  static success(title, message = '', options = {}) {
    return ReactSwal?.fire({
      icon: 'success',
      title: title,
      text: message,
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#10b981',
      timer: options?.autoClose ? 5000 : undefined,
      timerProgressBar: options?.autoClose || false,
      ...options
    });
  }

  /**
   * Show an error message
   * @param {string} title - Main title text
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   */
  static error(title, message = '', options = {}) {
    return ReactSwal?.fire({
      icon: 'error',
      title: title,
      text: message,
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#ef4444',
      ...options
    });
  }

  /**
   * Show a warning message
   * @param {string} title - Main title text
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   */
  static warning(title, message = '', options = {}) {
    return ReactSwal?.fire({
      icon: 'warning',
      title: title,
      text: message,
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#f59e0b',
      ...options
    });
  }

  /**
   * Show an info message
   * @param {string} title - Main title text
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   */
  static info(title, message = '', options = {}) {
    return ReactSwal?.fire({
      icon: 'info',
      title: title,
      text: message,
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3b82f6',
      ...options
    });
  }

  /**
   * Show a confirmation dialog
   * @param {string} title - Main title text
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   * @returns {Promise<boolean>} True if confirmed, false if cancelled
   */
  static async confirm(title, message = '', options = {}) {
    const result = await ReactSwal?.fire({
      icon: 'question',
      title: title,
      text: message,
      showCancelButton: true,
      confirmButtonText: options?.confirmText || 'Sí, continuar',
      cancelButtonText: options?.cancelText || 'Cancelar',
      confirmButtonColor: options?.confirmColor || '#10b981',
      cancelButtonColor: options?.cancelColor || '#6b7280',
      reverseButtons: true,
      focusCancel: true,
      ...options
    });
    
    return result?.isConfirmed;
  }

  /**
   * Show a delete confirmation dialog
   * @param {string} itemName - Name of the item to delete
   * @param {string} message - Optional detailed message
   * @param {object} options - Additional SweetAlert2 options
   * @returns {Promise<boolean>} True if confirmed, false if cancelled
   */
  static async confirmDelete(itemName = 'este elemento', message = '', options = {}) {
    const result = await ReactSwal?.fire({
      icon: 'warning',
      title: '¿Está seguro?',
      html: `
        <p>¿Desea eliminar <strong>${itemName}</strong>?</p>
        ${message ? `<p class="text-sm text-gray-600 mt-2">${message}</p>` : ''}
        <p class="text-sm text-red-600 mt-3">Esta acción no se puede deshacer.</p>
      `,
      showCancelButton: true,
      confirmButtonText: options?.confirmText || 'Sí, eliminar',
      cancelButtonText: options?.cancelText || 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true,
      ...options
    });
    
    return result?.isConfirmed;
  }

  /**
   * Show a loading message
   * @param {string} title - Loading message
   * @param {string} message - Optional detailed message
   */
  static loading(title = 'Procesando...', message = '') {
    return ReactSwal?.fire({
      title: title,
      text: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        ReactSwal?.showLoading();
      }
    });
  }

  /**
   * Close any open SweetAlert2 dialog
   */
  static close() {
    ReactSwal?.close();
  }

  /**
   * Show a toast notification (small popup)
   * @param {string} title - Toast message
   * @param {string} icon - Toast icon type
   * @param {object} options - Additional options
   */
  static toast(title, icon = 'info', options = {}) {
    const Toast = ReactSwal?.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: options?.timer || 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast?.addEventListener('mouseenter', ReactSwal?.stopTimer);
        toast?.addEventListener('mouseleave', ReactSwal?.resumeTimer);
      }
    });

    return Toast?.fire({
      icon: icon,
      title: title,
      ...options
    });
  }

  /**
   * Show a step-by-step process dialog
   * @param {Array} steps - Array of steps with title and content
   * @param {object} options - Additional options
   */
  static async steps(steps = [], options = {}) {
    const stepsContent = steps?.map((step, index) => ({
      title: `Paso ${index + 1}`,
      text: step?.title || `Paso ${index + 1}`,
      html: step?.content || step?.html || ''
    }));

    return ReactSwal?.queue(stepsContent);
  }

  /**
   * Show a form input dialog
   * @param {string} title - Form title
   * @param {string} inputType - Input type (text, email, password, textarea)
   * @param {object} options - Additional options
   */
  static async input(title, inputType = 'text', options = {}) {
    const result = await ReactSwal?.fire({
      title: title,
      input: inputType,
      inputPlaceholder: options?.placeholder || '',
      inputValue: options?.defaultValue || '',
      showCancelButton: true,
      confirmButtonText: options?.confirmText || 'Aceptar',
      cancelButtonText: options?.cancelText || 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      inputValidator: options?.validator || null,
      ...options
    });

    if (result?.isConfirmed) {
      return result?.value;
    }
    return null;
  }

  /**
   * Success Toast - Quick success notification
   */
  static successToast(message) {
    return this.toast(message, 'success', { 
      iconColor: '#10b981',
      background: '#f0fdf4',
      color: '#166534'
    });
  }

  /**
   * Error Toast - Quick error notification
   */
  static errorToast(message) {
    return this.toast(message, 'error', { 
      iconColor: '#ef4444',
      background: '#fef2f2',
      color: '#dc2626'
    });
  }

  /**
   * Warning Toast - Quick warning notification
   */
  static warningToast(message) {
    return this.toast(message, 'warning', { 
      iconColor: '#f59e0b',
      background: '#fffbeb',
      color: '#d97706'
    });
  }

  /**
   * Info Toast - Quick info notification
   */
  static infoToast(message) {
    return this.toast(message, 'info', { 
      iconColor: '#3b82f6',
      background: '#eff6ff',
      color: '#2563eb'
    });
  }
}

export default AlertService;